<template>
    <div v-if="isFirstLoading" style="height: 480px">
        <preloader :display="true" />
    </div>
    <div v-else>
        <div v-if="notifications.length > 0">
            <div class="notification-list-header">
                <h6 class="m-0 gray-60">{{ $filters.upperCase($l10n.__('Les plus récentes')) }}</h6>
                <CompassLink
                    v-if="canMarkAllAsRead"
                    :label="$t.__('notifications.NotificationsList.mark_all_as_read')"
                    type="secondary"
                    size="small"
                    @click="markAllAsRead"
                >
                </CompassLink>
            </div>
            <div class="notification-list-container" @scroll="onScroll">
                <notifications-item
                    v-for="notif in notifications"
                    :key="notif.notifId"
                    :item="notif"
                    @delete="deleteNotification"
                />

                <div v-if="isLoadingMore">
                    <preloader :display="true" />
                </div>
                <div v-if="noMore" class="no-more-container">
                    <div class="acenter no-more-content">
                        <img
                            src="/ressources/logged/img/notifications/no-results.svg"
                            alt=""
                            width="88"
                            height="88"
                        />
                        <p class="gray-60 fs-3 mt-0">
                            {{ $l10n.__("Vous n'avez pas d'autres notifications") }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="acenter notification-no-result">
            <img
                src="/ressources/logged/img/notifications/no-results.svg"
                alt=""
                width="184"
                height="184"
            />
            <div class="notification-no-result__text">
                <h4 class="gray-100 fs-4 mt-5">
                    {{ $l10n.__("Vous n'avez pas de notifications") }}
                </h4>
                <p class="gray-60 semibold mt-0">
                    {{
                        $l10n.__(
                            "Voici l'endroit où vous recevrez vos notifications en temps réel chaque fois que quelqu'un vous @mentionne",
                        )
                    }}
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import NotificationsItem from 'vcomponents/notifications/NotificationsItem.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import NotificationApi from 'api/notification/Notification';
import Notification from 'Services/Notification/Notification';
import ReminderNotification from 'Services/Notification/ActivityReminderNotification';
import { EVENT_TYPES } from 'Dictionaries/notification/NotificationEnums';

export default {
    components: {
        NotificationsItem,
        Preloader,
    },
    props: {
        unreadNotificationsCounter: {
            type: Number,
            required: false,
            default: 0,
        },
    },
    data() {
        return {
            notifications: [],
            isFirstLoading: true,
            isLoadingMore: true,
            pagination: null,
        };
    },
    computed: {
        noMore() {
            return this.notifications.length >= this.pagination.total;
        },
        disabled() {
            return this.isFirstLoading || this.isLoadingMore || this.noMore;
        },
        canMarkAllAsRead() {
            return this.unreadNotificationsCounter > 0;
        },
    },
    async created() {
        await this.getNotifications();
        EventBus.on('notification:mark-as-read', this.markAsRead);
        EventBus.on('notification:mark-as-unread', this.markAsUnRead);
        EventBus.on('notification:push', this.pushNotification);
    },
    unmounted() {
        EventBus.off('notification:mark-as-read', this.markAsRead);
        EventBus.off('notification:mark-as-unread', this.markAsUnRead);
        EventBus.off('notification:push', this.pushNotification);
    },
    methods: {
        onScroll: _.debounce(function (event) {
            const { scrollTop, clientHeight, scrollHeight, offsetTop } = event.target;
            if (this.disabled) {
                return;
            }
            if (Math.ceil(scrollTop) + clientHeight + offsetTop >= scrollHeight) {
                this.loadMoreNotification();
            }
        }, 100),
        async getNotifications() {
            try {
                this.isFirstLoading = true;
                const AllNotifications = await NotificationApi.getNotifications({});
                this.notifications = AllNotifications.data.data.map((notif) => {
                    if (notif.event === EVENT_TYPES.AGENDA_ALERT_SENT) {
                        return new ReminderNotification(notif);
                    }
                    return new Notification(notif);
                });
                this.pagination = AllNotifications.data.pagination;
            } catch (error) {
                Push.error(
                    this.$l10n.__(
                        'Une erreur est survenue lors de la récupération des notifications.',
                    ),
                );
            } finally {
                this.isFirstLoading = false;
                this.isLoadingMore = false;
            }
        },
        loadMoreNotification() {
            this.isLoadingMore = true;
            NotificationApi.getNotifications({ offset: this.pagination.offset })
                .then((res) => {
                    const moreNotifications = res.data.data.map((notif) => {
                        if (notif.event === EVENT_TYPES.AGENDA_ALERT_SENT) {
                            return new ReminderNotification(notif);
                        }
                        return new Notification(notif);
                    });
                    this.notifications = this.notifications.concat(moreNotifications);
                    this.pagination = res.data.pagination;
                    this.isLoadingMore = false;
                })
                .catch(() => {
                    Push.error(
                        this.$l10n.__(
                            'Une erreur est survenue lors de la récupération des notifications.',
                        ),
                    );
                });
        },
         
        markAsRead(id) {
            const notif = this.notifications.find((notif) => notif.notifId === id);
            const readStatus = notif.read;
            if (readStatus) return;

            NotificationApi.markAsRead(id)
                .then(() => {
                    if (notif !== undefined) {
                        notif.read = true;
                        EventBus.emit('notification:unread:decrement');
                    }
                })
                .catch((err) => {
                    Push.error(err.message);
                });
        },
         
        markAsUnRead(id) {
            const notif = this.notifications.find((notif) => notif.notifId === id);
            const readStatus = notif.read;
            if (!readStatus) return;

            NotificationApi.markAsUnRead(id)
                .then(() => {
                    if (notif !== undefined) {
                        notif.read = null;
                        EventBus.emit('notification:unread:increment');
                    }
                })
                .catch((err) => {
                    Push.error(err.message);
                });
        },
        deleteNotification(id) {
            NotificationApi.delete(id).then(() => {
                const notif = this.notifications.find((notif) => notif.notifId === id);
                if (notif !== undefined) {
                    if (notif.read === null) {
                        EventBus.emit('notification:unread:decrement');
                    }
                    this.notifications = this.notifications.filter((notif) => notif.notifId !== id);
                }
            });
        },
         
        pushNotification(data) {
            this.notifications.unshift(data);
        },
        markAllAsRead: _.debounce(function () {
            if (this.canMarkAllAsRead) {
                NotificationApi.markAllAsRead()
                    .then(() => {
                        this.notifications
                            .filter((notif) => notif.read === null)
                            .forEach((notif) => {
                                notif.read = true;
                            });
                        EventBus.emit('notification:unread:resetUnreadNotifCounter');
                    })
                    .catch((err) => {
                        Push.error(err.message);
                    });
            }
        }, 500),
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.notification-list-container {
    height: 500px;
    margin-bottom: 0;
    overflow-y: scroll;

    .no-more-container {
        padding: 40px;

        .no-more-content {
            margin: auto;
            width: 146px;
            word-break: initial;
        }
    }
}

.notification-no-result {
    padding: 160px 40px;
    word-break: break-word;

    .notification-no-result__text {
        margin: auto;
        width: 360px;
    }
}

.notification-list-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    margin-top: 16px;
    padding: 0 16px;

    h6 {
        font-weight: 700;
        line-height: 16px;
    }
}
</style>
